/**
 * AP Basin Form - the Basin-backed enquiry form's runtime.
 *
 * Began as a deliberate fork of the retired Formspree runtime rather than a shared
 * implementation, so the Formspree section, its JS and the two vendored @formspree
 * modules could be deleted outright once Basin-to-Gorgias was proven. That deletion
 * landed on 7 Sep 2026 (drawer/header audit F10); this is now the only form runtime.
 *
 * The `data-ap-form-*` attribute names and `.ap-enquiry-form__*` classes are unchanged
 * because the 11 shared blocks/_ap-form-*.liquid field blocks emit them. Renaming that
 * contract is a single mechanical pass across the blocks, the section, this file and
 * base.css, deliberately not bundled with the deletion.
 *
 * What changed from the Formspree original, kept for the record:
 * - No SDK. One fetch to Basin, with the DOM contract driven from here.
 * - Errors are form-level only. Basin replies {"error": "<one string>"} with no field
 *   key, so the per-field [data-ap-form-error="<name>"] spans have no server data to render.
 *   They stay live for HTML5 constraint validation and this element's own guards.
 * - Date fields are formatted inside the FormData rather than by swapping input names
 *   around the SDK's synchronous snapshot - there is no snapshot to race here.
 * - Symmetric lifecycle: one AbortController per connect, aborted on disconnect.
 */
import { publish } from '@theme/ap-analytics';

const BASIN_ENDPOINT = 'https://usebasin.com/f/';
// Basin accepts files up to 100 MB each.
const MAX_FILE_BYTES = 100 * 1024 * 1024;
// A short beat after success before the confirmation fades in, so the "Sending..."
// state registers and the message reads as the result of real work, not a no-op.
const SUCCESS_SHOW_DELAY_MS = 350;
const GENERIC_ERROR = 'Your message could not be sent. Please try again, or email us directly.';
// Plumbing the customer should never read in a quoted reply: the honeypot, the Gorgias tag
// list, the composed subject (it is already the ticket subject) and the summaries themselves.
// Everything else is still submitted and still visible in Basin's own submission record.
const INTERNAL_FIELDS = new Set(['tags', 'Subject']);
// Everything this element adds for the webhook's benefit is underscore-prefixed, so one
// prefix check covers _gotcha, the summaries, the customer identity and the tag flags -
// and covers anything added later without a second place to update.
const isInternalField = (name) => name.startsWith('_') || INTERNAL_FIELDS.has(name);

const escapeHtml = (value) =>
  value.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

// Separates the free-text message from the structured fields in the ticket body.
const DIVIDER_TEXT = '-'.repeat(40);

// Normalises a tag to a Gorgias-friendly handle: lowercase, ASCII, hyphen-separated
// (e.g. "Online Purchase" -> "online-purchase"). Mirrors Shopify's handleize.
const handleize = (value) =>
  value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
const DATE_FORMAT = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

class ApBasinForm extends HTMLElement {
  #abortController = null;
  #overlayShowTimer = null;
  #overlayHideTimer = null;
  #submitting = false;

  connectedCallback() {
    const form = this.querySelector('form');
    if (!form) return;

    const controller = new AbortController();
    this.#abortController = controller;
    const { signal } = controller;

    this.#warnIfMisconfigured(form);
    this.#supersedeAutoProductField(form);

    for (const input of form.querySelectorAll('input[data-ap-form-file]')) {
      input.addEventListener('change', () => this.#syncFileStatus(input), { signal });
    }

    for (const control of form.querySelectorAll('.ap-enquiry-form__select, .ap-enquiry-form__date')) {
      this.#syncPlaceholderState(control);
      control.addEventListener('change', () => this.#syncPlaceholderState(control), { signal });
      control.addEventListener('input', () => this.#syncPlaceholderState(control), { signal });
    }

    for (const group of form.querySelectorAll('[data-ap-form-checkbox-group]')) {
      group.addEventListener(
        'change',
        (event) => {
          if (event.target.matches('input[type="checkbox"]')) this.#clearCheckboxGroupError(group);
        },
        { signal }
      );
    }

    form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this.#onSubmit(form);
      },
      { signal }
    );
  }

  // Every side effect connectedCallback sets up is torn down here: the listeners share
  // one AbortController (which also cancels an in-flight submit), and the two overlay
  // timers are cleared so they cannot fire on a detached node. Aborting rather than
  // relying on an init guard means a detach/reattach re-binds cleanly instead of
  // leaving a half-live element.
  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    clearTimeout(this.#overlayShowTimer);
    clearTimeout(this.#overlayHideTimer);
    this.#overlayShowTimer = null;
    this.#overlayHideTimer = null;
    this.#submitting = false;
  }

  async #onSubmit(form) {
    if (this.#submitting) return;

    this.#resetValidationUi(form);
    this.#syncMessages(form);
    this.#mergeSubjectAndTags(form);

    const oversize = this.#findOversizeFile(form);
    if (oversize) {
      this.#showFieldError(
        form,
        oversize.input,
        `"${oversize.file.name}" is too large - attachments must be under 100 MB.`
      );
      return;
    }

    const missingCheckboxGroup = this.#findMissingCheckboxGroup(form);
    if (missingCheckboxGroup) {
      this.#showCheckboxGroupError(missingCheckboxGroup);
      return;
    }

    const formId = this.dataset.formId?.trim();
    if (!formId) {
      this.#renderFormError(form, GENERIC_ERROR);
      this.#handleFailure(form);
      return;
    }

    this.#submitting = true;
    this.#setBusy(form, true);

    // Only the request is guarded. Wrapping the success handler too would let a throw
    // inside it be reported to the customer as a network failure after their enquiry
    // had already been delivered.
    let response = null;
    let networkError = null;
    try {
      // Only Accept is set. Basin's preflight allows exactly `accept,content-type`, so an
      // extra header fails CORS - and setting Content-Type by hand with a FormData body
      // would destroy the multipart boundary the browser generates.
      response = await fetch(`${BASIN_ENDPOINT}${encodeURIComponent(formId)}`, {
        method: 'POST',
        body: this.#buildPayload(form),
        headers: { Accept: 'application/json' },
        signal: this.#abortController?.signal,
      });
    } catch (error) {
      networkError = error;
    } finally {
      this.#submitting = false;
      this.#setBusy(form, false);
    }

    if (networkError) {
      // An abort means the element was detached mid-flight; there is no UI left to update.
      if (networkError?.name === 'AbortError') return;
      this.#renderFormError(form, 'Your message could not be sent. Please check your connection and try again.');
      this.#handleFailure(form);
      return;
    }

    if (response.ok) {
      this.#onSuccess(form);
      return;
    }

    this.#renderFormError(form, await this.#readErrorMessage(response));
    this.#handleFailure(form);
  }

  /**
   * Snapshots the form, formatting date fields on the way through so the Basin
   * notification reads "12 September 2026" rather than the input's ISO value. Without
   * JavaScript the date input submits ISO, which is the same fallback as before.
   * @param {HTMLFormElement} form - The form being submitted.
   * @returns {FormData} The payload to post.
   */
  #buildPayload(form) {
    const formData = new FormData(form);
    for (const input of form.querySelectorAll('input[type="date"][name]')) {
      if (!input.value) continue;
      formData.set(input.name, DATE_FORMAT.format(new Date(`${input.value}T00:00:00`)));
    }
    // Identity is lifted out so the webhook can put it on the Gorgias *customer* rather than
    // leaving it buried in the message body. Set before the summary so both are excluded there.
    const { name, phone } = this.#customerIdentity(form);
    if (name) formData.set('_customer_name', name);
    if (phone) formData.set('_customer_phone', phone);
    this.#setTagFlags(form, formData);

    // Gorgias requires a content_type on every attachment it fetches, and Basin's webhook
    // exposes only name / public_url / file_size — no MIME type. The browser knows it, so it
    // travels with the submission. Empty file inputs still yield a zero-byte File, hence the
    // size check. Forms here take a single file; if one ever accepts several of differing
    // types they would all be labelled with the first, which affects previewing only — the
    // template falls back to application/octet-stream regardless.
    const upload = [...formData.values()].find((value) => value instanceof File && value.size > 0);
    if (upload?.type) formData.set('_attachment_content_type', upload.type);

    // Composed after the date pass so the summary shows "1 October 2026", not the ISO value.
    const { text, html } = this.#composeSummary(form, formData);
    formData.set('_summary', text);
    formData.set('_summary_html', html);
    return formData;
  }

  /**
   * Builds the ticket body here rather than in Basin's webhook template, for three reasons:
   * the template's only escape helper (JSONstringify) emits surrounding quotes, so looped
   * values arrive wrapped in `"…"`; nothing in that helper set can escape a newline inside a
   * JSON string, so a multi-line message would produce invalid JSON and lose the enquiry; and
   * the template only sees submitted field *names* (`firstName`), whereas the DOM has the
   * merchant's real labels ("First name"). Both summaries land in the payload as a single
   * JSON value each, so the template needs no escaping gymnastics at all.
   * @param {HTMLFormElement} form - The form being submitted.
   * @param {FormData} formData - The payload, already date-formatted.
   * @returns {{text: string, html: string}} Plain-text and HTML renderings.
   */
  #composeSummary(form, formData) {
    /** @type {Map<string, {values: string[], isMessage: boolean}>} */
    const rows = new Map();

    for (const [name, value] of formData.entries()) {
      if (isInternalField(name)) continue;
      // File inputs yield File objects. Basin's hosted URLs don't exist yet at submit time,
      // so the summary names the file and the webhook template carries the links.
      const rendered = value instanceof File ? value.name : String(value).trim();
      if (!rendered) continue;
      const control = form.querySelector(`[name="${CSS.escape(name)}"]`);
      const label = this.#labelFor(form, control) || name;
      let row = rows.get(label);
      if (!row) {
        row = { values: [], isMessage: control instanceof HTMLTextAreaElement };
        rows.set(label, row);
      }
      row.values.push(rendered);
    }

    // The free-text message leads: it is what a consultant reads first, and the structured
    // fields below it are reference. Identified by the control being a <textarea> rather than
    // by field name, so renaming or re-labelling the block in the theme editor can't break it.
    const entries = [...rows].map(([label, row]) => ({
      label,
      value: row.values.join(', '),
      isMessage: row.isMessage,
    }));
    const message = entries.filter((entry) => entry.isMessage);
    const rest = entries.filter((entry) => !entry.isMessage);
    const divide = message.length > 0 && rest.length > 0;

    // The message is unlabelled: it leads the ticket, so a "Message:" prefix reads as noise.
    // The structured fields below the divider keep their labels.
    const textParts = message.map((entry) => entry.value);
    if (divide) textParts.push(DIVIDER_TEXT);
    textParts.push(...rest.map((entry) => `${entry.label}: ${entry.value}`));

    const asHtml = (value) => escapeHtml(value).replace(/\r?\n/g, '<br>');
    const htmlParts = message.map((entry) => `<p>${asHtml(entry.value)}</p>`);
    if (divide) htmlParts.push('<hr>');
    htmlParts.push(
      ...rest.map((entry) => `<p><strong>${escapeHtml(entry.label)}:</strong> ${asHtml(entry.value)}</p>`)
    );

    return { text: textParts.join('\n'), html: htmlParts.join('') };
  }

  /**
   * Emits one flag field per Gorgias tag, so the webhook template can build Gorgias's
   * array-of-objects shape with **literal** tag names: `{{#if _tag_online_purchase}}`.
   *
   * Basin's template language has no `eq` and no `split`, so it cannot compare or divide the
   * comma-joined `tags` string. Interpolating that string into the tag array would work, but
   * it would let a direct POST mint arbitrary Gorgias tags. Flags invert that: a submitter can
   * set any flag they like and still only unlock the literals the template already names.
   *
   * Handlebars treats a hyphen as subtraction inside a variable name, so handles are
   * underscored here (`online-purchase` -> `_tag_online_purchase`).
   * @param {HTMLFormElement} form - The form being submitted.
   * @param {FormData} formData - The payload to add the flags to.
   */
  #setTagFlags(form, formData) {
    const tags = form.querySelector('input[data-ap-form-tags]');
    if (!(tags instanceof HTMLInputElement)) return;
    for (const entry of tags.value.split(',')) {
      const handle = entry.trim();
      if (handle) formData.set(`_tag_${handle.replace(/-/g, '_')}`, handle);
    }
  }

  /**
   * The customer's name and phone, read from the standard `autocomplete` tokens the field
   * blocks already emit (`given-name` / `family-name` / `name` / `tel`, set from each block's
   * Prefill option). Keying on those rather than field names means renaming a field in the
   * theme editor cannot break this, and it is the same contract browsers autofill against.
   * @param {HTMLFormElement} form - The form being submitted.
   * @returns {{name: string, phone: string}} Blank strings when the form has no such field.
   */
  #customerIdentity(form) {
    const read = (token) => {
      const control = form.querySelector(`[autocomplete="${token}"]`);
      return control instanceof HTMLInputElement ? control.value.trim() : '';
    };
    const full = read('name');
    return {
      name: full || [read('given-name'), read('family-name')].filter(Boolean).join(' '),
      phone: read('tel'),
    };
  }

  /**
   * The merchant-facing label for a control, or '' when it has none.
   * Checkbox and radio groups carry their label on a legend rather than a <label for>.
   * @param {HTMLFormElement} form - The form being submitted.
   * @param {Element | null} control - The control the value came from.
   * @returns {string} A human label, or '' to fall back to the field name.
   */
  #labelFor(form, control) {
    if (!control) return '';

    const legend = control.closest('fieldset')?.querySelector('legend');
    if (legend) return this.#cleanLabel(legend.textContent);

    const label = control.id ? form.querySelector(`label[for="${CSS.escape(control.id)}"]`) : null;
    return label ? this.#cleanLabel(label.textContent) : '';
  }

  // Collapses whitespace, then drops the required-marker asterisk the label carries in a
  // <span aria-hidden="true">. Trimming must happen BEFORE the asterisk strip: collapsing
  // leaves a trailing space after it, which stops the $-anchored pattern from matching.
  #cleanLabel(text) {
    return (text || '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\s*\*$/, '')
      .trim();
  }

  /**
   * Basin replies {"error": "<one human string>"} with a 400. There is no errors array
   * and no field key, so this is the whole of the server's error vocabulary.
   * @param {Response} response - The failed response.
   * @returns {Promise<string>} A message to show the customer.
   */
  async #readErrorMessage(response) {
    try {
      const body = await response.json();
      for (const value of [body?.error, body?.message]) {
        if (typeof value === 'string' && value.trim()) return value.trim();
      }
    } catch {
      // Non-JSON body - fall through to the generic message.
    }
    return GENERIC_ERROR;
  }

  #onSuccess(form) {
    this.#renderSuccess();
    this.#syncMessages(form);
    // Published before the reset below empties the fields the payload reads.
    this.#publishSubmission(form);
    // Clear the form only once the overlay has fully faded in, so the fields
    // don't visibly empty before the confirmation appears.
    this.#showSuccessOverlay(() => {
      form.reset();
      for (const input of form.querySelectorAll('input[data-ap-form-file]')) {
        this.#syncFileStatus(input);
      }
      this.#syncPlaceholderStates(form);
      this.#syncMessages(form);
    });
  }

  // Drives the submit button's "Sending..." state during the request — the
  // same data-adding crossfade the theme's add-to-cart button uses.
  #setBusy(form, busy) {
    form.toggleAttribute('aria-busy', busy);
    for (const button of form.querySelectorAll('[data-ap-form-submit-btn]')) {
      button.disabled = busy;
      if (busy) {
        button.dataset.adding = 'true';
      } else {
        delete button.dataset.adding;
      }
    }
  }

  // The section always renders a hidden "Product" field derived from the handle. When a
  // Product context block is also present it submits the real product title under the
  // same name, so the section's field is disabled here (disabled inputs are not
  // serialised) rather than removed — nothing is torn out of the DOM, and the state is
  // trivially reversible. Liquid cannot make this decision: in the theme-blocks
  // architecture a section cannot see which block types its `content_for 'blocks'`
  // rendered.
  #supersedeAutoProductField(form) {
    if (!form.querySelector('[data-ap-form-product-context]')) return;
    for (const input of form.querySelectorAll('input[data-ap-form-auto-product]')) {
      input.disabled = true;
    }
  }

  // Both of these are merchant configuration mistakes that are invisible on the
  // storefront, so they surface in the console always and in the theme editor visibly.
  #warnIfMisconfigured(form) {
    if (!this.dataset.formId?.trim()) {
      console.warn('[ap-basin-form] No Basin form ID set — the form cannot send.');
      this.#showEditorNotice(form, 'This form has no Basin form ID — set it in the section settings.');
    }

    if (!form.querySelector('input[type="email"]')) {
      console.warn('[ap-basin-form] No email block found — add the Email block so replies have an address.');
      this.#showEditorNotice(form, 'This form has no Email block — add one so replies have an address to go to.');
    }
  }

  #showEditorNotice(form, message) {
    if (!window.Shopify?.designMode) return;
    if ([...form.querySelectorAll('[data-ap-form-editor-notice]')].some((el) => el.textContent === message)) return;
    const notice = document.createElement('div');
    notice.className = 'contact-form__error ap-enquiry-form__message ap-enquiry-form__message--error';
    notice.dataset.apFormEditorNotice = '';
    notice.textContent = message;
    form.prepend(notice);
  }

  // Clears every message this element can activate, so nothing from the previous attempt
  // survives into the next one. Queried from the host, not the form, because the success
  // node deliberately lives outside <form>.
  #resetValidationUi(form) {
    for (const span of this.querySelectorAll('[data-ap-form-error], [data-ap-form-success]')) {
      span.removeAttribute('data-ap-form-active');
      if (span.hasAttribute('data-ap-form-manual')) {
        span.textContent = '';
        span.removeAttribute('data-ap-form-manual');
      }
    }
    for (const field of form.querySelectorAll('[data-ap-form-field][aria-invalid]')) {
      field.removeAttribute('aria-invalid');
    }
  }

  // The banner wrappers (icon + span) show or hide as a unit via their hidden attribute,
  // following whether the span inside them is active.
  #syncMessages(form) {
    // Editor notices borrow the same wrapper class for styling but hold no message span,
    // so they are excluded or the first submit would hide the very warning being shown.
    for (const wrapper of form.querySelectorAll('.ap-enquiry-form__message:not([data-ap-form-editor-notice])')) {
      wrapper.toggleAttribute('hidden', !wrapper.querySelector('[data-ap-form-active]'));
    }
  }

  // [data-ap-form-success] is a sibling of <form>, not a descendant — it sits inside the
  // overlay so it adds no flow height and the confirmation causes no layout shift.
  #renderSuccess() {
    this.querySelector('[data-ap-form-success]')?.setAttribute('data-ap-form-active', '');
  }

  #renderFormError(form, message) {
    const span = this.querySelector('[data-ap-form-error=""]');
    if (!span) return;
    span.textContent = message;
    span.setAttribute('data-ap-form-active', '');
    span.setAttribute('data-ap-form-manual', '');
    this.#syncMessages(form);
  }

  /**
   * Reads one value from the Product context block, or '' when the form has none.
   * @param {HTMLFormElement} form - The form being submitted.
   * @param {string} key - The data-context-input key ('product' | 'colour' | 'size' | 'url').
   * @returns {string} The trimmed value.
   */
  #productContextValue(form, key) {
    const input = form.querySelector(`[data-ap-form-product-context] [data-context-input="${key}"]`);
    return input instanceof HTMLInputElement ? input.value.trim() : '';
  }

  // Conversion counterpart to the ap_sold_out_enquiry_shown / _click impression events
  // (assets/ap-variant-analytics.js). Published for every AP enquiry form, with the
  // section's Gorgias tags identifying which one — so the funnel closes without any
  // per-form wiring. Product/colour/size are present only when a Product context block is.
  /** @param {HTMLFormElement} form - The form that submitted successfully. */
  #publishSubmission(form) {
    const tags = form.querySelector('input[data-ap-form-tags]');
    publish('ap_enquiry_submitted', {
      form_tags: tags instanceof HTMLInputElement ? tags.value : '',
      product: this.#productContextValue(form, 'product') || null,
      colour: this.#productContextValue(form, 'colour') || null,
      size: this.#productContextValue(form, 'size') || null,
    });
  }

  #focusWrapperOf(span) {
    const target = span && (span.closest('.ap-enquiry-form__message') || span);
    if (target) target.focus();
  }

  // Fades a full-form confirmation overlay in (covering the reset form so there's
  // no layout shift), holds it, then fades it back out. The success text inside
  // is the same [data-ap-form-success] node activated above, so it's already shown.
  // Focusing the message both announces it (role="status") and scrolls it into
  // view when the form is long or in a drawer. Hold is merchant-set; default 4s.
  #showSuccessOverlay(onCovered) {
    const overlay = this.querySelector('[data-ap-form-overlay]');
    if (!overlay) {
      onCovered?.();
      return;
    }
    clearTimeout(this.#overlayShowTimer);
    clearTimeout(this.#overlayHideTimer);

    const seconds = Number(this.dataset.successHold);
    const hold = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 6000;

    this.#overlayShowTimer = setTimeout(() => {
      overlay.classList.add('is-visible');
      overlay.querySelector('[data-ap-form-overlay-message]')?.focus();
      // Run the reset after the fade-in finishes, while the overlay is opaque.
      this.#runWhenOverlayOpaque(overlay, onCovered);
      this.#overlayHideTimer = setTimeout(() => overlay.classList.remove('is-visible'), hold);
    }, SUCCESS_SHOW_DELAY_MS);
  }

  // Reads the live transition-duration (so it tracks the overlay-speed setting and
  // reduced-motion's 0s) and runs the callback once the fade-in has completed.
  #runWhenOverlayOpaque(overlay, callback) {
    if (!callback) return;
    const durations = getComputedStyle(overlay)
      .transitionDuration.split(',')
      .map((value) => (parseFloat(value) || 0) * 1000);
    setTimeout(callback, Math.max(0, ...durations) + 50);
  }

  #handleFailure(form) {
    this.#syncMessages(form);
    const formError = this.querySelector('[data-ap-form-error=""][data-ap-form-active]');
    if (formError) {
      this.#focusWrapperOf(formError);
      return;
    }
    const invalidField = form.querySelector('[aria-invalid="true"]');
    if (invalidField) invalidField.focus();
  }

  // A dropdown marked "append to subject" feeds the hidden Subject field. When the
  // template carries a {topic} placeholder the selection is substituted in place
  // (e.g. "Alex Perry - Returns Enquiry"); otherwise it's appended after the base
  // (e.g. "Alex Perry Enquiry - Returns") for backward compatibility. Basin has no
  // reserved _subject field, so this is an ordinary field that Basin's notification
  // template reads as a merge tag — the marker attribute is how it's found here.
  // The hidden tags field (read by the Basin to Gorgias route, comma-separated)
  // always carries the section's base tags plus every tag-source dropdown's selection.
  #mergeSubjectAndTags(form) {
    const subjectInput = form.querySelector('input[data-ap-form-subject]');
    if (subjectInput) {
      const template = (subjectInput.dataset.apFormSubjectTemplate ?? subjectInput.defaultValue).trim();
      const extras = [...form.querySelectorAll('[data-ap-form-subject-append]')]
        .map((select) => select.value.trim())
        .filter(Boolean);
      const subject = template.includes('{topic}')
        ? template.replace('{topic}', extras.join(' - '))
        : [template, ...extras].filter(Boolean).join(' - ');
      // {product} is filled from the Product context block, so a sold-out enquiry lands in
      // Gorgias already naming the piece. A trailing separator is trimmed for the case
      // where the template ends in "- {product}" and no context block supplied a value.
      subjectInput.value = subject
        .replace('{product}', this.#productContextValue(form, 'product'))
        .replace(/\s+/g, ' ')
        .replace(/\s*[—-]\s*$/, '')
        .trim();
    }

    const tagsInput = form.querySelector('input[data-ap-form-tags]');
    if (tagsInput) {
      // Base tags can be a comma-separated list, so split before handleizing;
      // each tag-source dropdown contributes one tag. All are handleized
      // (lowercase, hyphenated) and de-duplicated, preserving order.
      const tags = [
        ...tagsInput.defaultValue.split(','),
        ...[...form.querySelectorAll('select[data-ap-form-tag-source]')].map((select) => select.value),
      ]
        .map(handleize)
        .filter(Boolean);
      tagsInput.value = [...new Set(tags)].join(', ');
    }
  }

  // The native file input is visually hidden behind a styled button, so its
  // "No file chosen" text is replaced by a status line we keep in sync.
  #syncFileStatus(input) {
    const status = input.closest('.contact-form__field')?.querySelector('[data-ap-form-file-status]');
    if (!status) return;
    const names = [...input.files].map((file) => file.name).join(', ');
    status.textContent = names || status.dataset.emptyText || '';
  }

  #syncPlaceholderStates(form) {
    for (const control of form.querySelectorAll('.ap-enquiry-form__select, .ap-enquiry-form__date')) {
      this.#syncPlaceholderState(control);
    }
  }

  #syncPlaceholderState(control) {
    control.toggleAttribute('data-ap-form-placeholder-active', !control.value);
  }

  #findOversizeFile(form) {
    for (const input of form.querySelectorAll('input[type="file"]')) {
      for (const file of input.files) {
        if (file.size > MAX_FILE_BYTES) return { input, file };
      }
    }
    return null;
  }

  #findMissingCheckboxGroup(form) {
    for (const group of form.querySelectorAll('[data-ap-form-checkbox-group][data-ap-form-required]')) {
      if (!group.querySelector('input[type="checkbox"]:checked')) return group;
    }
    return null;
  }

  #clearCheckboxGroupError(group) {
    if (!group.querySelector('input[type="checkbox"]:checked')) return;
    const span = group.querySelector('[data-ap-form-error]');
    if (span?.hasAttribute('data-ap-form-manual')) {
      span.textContent = '';
      span.removeAttribute('data-ap-form-active');
      span.removeAttribute('data-ap-form-manual');
    }
    for (const input of group.querySelectorAll('input[type="checkbox"]')) {
      input.removeAttribute('aria-invalid');
    }
  }

  #showCheckboxGroupError(group) {
    const span = group.querySelector('[data-ap-form-error]');
    const legend = group.querySelector('legend')?.textContent.trim().replace(/\s*\*$/, '') || 'This field';
    if (span) {
      span.textContent = `${legend} is required.`;
      span.setAttribute('data-ap-form-active', '');
      span.setAttribute('data-ap-form-manual', '');
    }
    const firstInput = group.querySelector('input[type="checkbox"]');
    for (const input of group.querySelectorAll('input[type="checkbox"]')) {
      input.setAttribute('aria-invalid', 'true');
    }
    firstInput?.focus();
  }

  #showFieldError(form, input, message) {
    for (const span of form.querySelectorAll('[data-ap-form-error]')) {
      if (span.dataset.apFormError !== input.name) continue;
      span.textContent = message;
      span.setAttribute('data-ap-form-active', '');
      span.setAttribute('data-ap-form-manual', '');
    }
    input.setAttribute('aria-invalid', 'true');
    input.focus();
  }
}

/**
 * Product / colour / size rows inside the form (blocks/_ap-form-product-context.liquid).
 *
 * The block server-renders the template product's own values, so the rows are already
 * right for the theme editor, for inline (non-drawer) use, and for the single-variant
 * Bride form. When the form is opened from an enquiry trigger,
 * assets/ap-enquiry-trigger.js calls applyContext() with that trigger's dataset, which
 * carries the display labels Liquid rendered for the variant the shopper actually
 * selected — so the size shown here (and in the Gorgias ticket) is "US 0/UK 4", never a
 * raw option value recomputed in JS.
 *
 * Originally duplicated from the Formspree runtime rather than extracted to a shared
 * module so that file could be deleted cleanly; with that runtime gone this is the only
 * definition. The registration guard below stays as ordinary custom-element hygiene.
 *
 * No lifecycle work: the element has no listeners, observers or timers, so it needs no
 * disconnectedCallback. It is a plain method target.
 */
class ApFormProductContext extends HTMLElement {
  /**
   * Copies a trigger's data-context-* values onto the rows and hidden inputs.
   * Keys the trigger omits are left at their server-rendered values.
   * @param {DOMStringMap | Record<string, string | undefined>} data - The trigger's dataset.
   */
  applyContext(data = {}) {
    /** @type {Record<string, string | undefined>} */
    const values = {
      product: data.contextProduct,
      colour: data.contextColour,
      size: data.contextSize,
      url: data.contextUrl,
    };

    for (const [key, value] of Object.entries(values)) {
      if (value === undefined) continue;
      this.#setValue(key, value);
    }
  }

  /**
   * @param {string} key - The context key.
   * @param {string} value - The new value; blank hides the row and drops the field.
   */
  #setValue(key, value) {
    const trimmed = value.trim();
    const display = this.querySelector(`[data-context="${key}"]`);
    const input = this.querySelector(`[data-context-input="${key}"]`);
    const row = this.querySelector(`[data-context-row="${key}"]`);

    if (display) display.textContent = trimmed;
    if (input instanceof HTMLInputElement) {
      input.value = trimmed;
      // Blank fields are submitted disabled so the notification email has no empty rows.
      input.disabled = trimmed === '';
    }
    if (row) row.toggleAttribute('hidden', trimmed === '');
  }
}

if (!customElements.get('ap-basin-form')) {
  customElements.define('ap-basin-form', ApBasinForm);
}

if (!customElements.get('ap-form-product-context')) {
  customElements.define('ap-form-product-context', ApFormProductContext);
}
